import {
  Button,
  HStack,
  Table,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
  VStack,
  Badge,
  Box,
  Flex,
  Text,
  useBreakpointValue,

  Stack,
  Card,
  CardBody,
  Divider,

  Tooltip,
  Center,
  CircularProgress,
} from "@chakra-ui/react";
import {
  faAdd,
  faEye,
  faPen,
  faToggleOff,
  faToggleOn,
  faExclamationTriangle,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React, { useContext, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { getMyDiscounts } from "../api/discounts";
import { GraphBox } from "../components/molecules/GraphBox";
import { ToggleDiscountModal } from "../components/organisms/ToggleDiscountModal";
import { DiscountModal } from "../components/organisms/DiscountModal";
import { StoreStatistics } from "../components/organisms/StoreStatistics";
import { CompanyContext } from "../contexts/CompanyContext";
import { DashboardTemplate } from "../templates/DashboardTemplate";
import type { Discount } from "../types/global";

// Types for the component
interface DiscountTypeLabels {
  buy_and_gain: string;
  treat: string;
  discount: string;
}

interface StatusBadgeProps {
  discount: Discount;
}

// Component for status badge
const StatusBadge: React.FC<StatusBadgeProps> = ({ discount }) => {
  if (discount.pending_approval) {
    return (
      <Badge colorScheme="yellow" variant="subtle">
        <FontAwesomeIcon icon={faExclamationTriangle} style={{ marginRight: "4px" }} />
        Em Análise
      </Badge>
    );
  }
  
  if (discount.active) {
    return (
      <Badge colorScheme="green" variant="subtle">
        Aprovado
      </Badge>
    );
  }
  
  return (
    <Badge colorScheme="red" variant="subtle">
      Desativado
    </Badge>
  );
};

// Component for discount actions
interface DiscountActionsProps {
  discount: Discount;
  company: any;
  store: any;
  onEdit: (discount: Discount) => void;
  onToggle: (discount: Discount) => void;
}

const DiscountActions: React.FC<DiscountActionsProps> = ({
  discount,
  company,
  store,
  onEdit,
  onToggle
}) => {
  const isMobile = useBreakpointValue({ base: true, md: false });
  
  const handleView = () => {
    window.open(
      `https://app.partiu.com.br/detalhes/${discount.mode}/${
        discount.mode === "local" ? store.id : discount.id
      }/`,
      "_blank"
    );
  };

  if (isMobile) {
    return (
      <Stack spacing={2} direction="column" width="full">
        <Button
          size="sm"
          colorScheme="blue"
          variant="outline"
          isDisabled={
            company.status !== "approved" ||
            discount.pending_approval ||
            !discount.active
          }
          leftIcon={<FontAwesomeIcon icon={faEye} />}
          onClick={handleView}
          width="full"
        >
          Ver
        </Button>
        <Button
          size="sm"
          colorScheme="green"
          variant="outline"
          leftIcon={<FontAwesomeIcon icon={faPen} />}
          onClick={() => onEdit(discount)}
          width="full"
        >
          Editar
        </Button>
        {discount.active ? (
          <Button
            size="sm"
            colorScheme="red"
            variant="outline"
            isDisabled={discount.pending_approval}
            onClick={() => onToggle(discount)}
            leftIcon={<FontAwesomeIcon icon={faToggleOff} />}
            width="full"
          >
            Desativar
          </Button>
        ) : (
          <Button
            size="sm"
            colorScheme="green"
            isDisabled={discount.pending_approval}
            onClick={() => onToggle(discount)}
            leftIcon={<FontAwesomeIcon icon={faToggleOn} />}
            width="full"
          >
            Reativar
          </Button>
        )}
      </Stack>
    );
  }

  return (
    <HStack spacing={2}>
      <Tooltip label="Visualizar desconto no app">
        <Button
          size="sm"
          colorScheme="blue"
          variant="outline"
          isDisabled={
            company.status !== "approved" ||
            discount.pending_approval ||
            !discount.active
          }
          leftIcon={<FontAwesomeIcon icon={faEye} />}
          onClick={handleView}
        >
          Ver
        </Button>
      </Tooltip>
      <Tooltip label="Editar desconto">
        <Button
          size="sm"
          colorScheme="green"
          variant="outline"
          leftIcon={<FontAwesomeIcon icon={faPen} />}
          onClick={() => onEdit(discount)}
        >
          Editar
        </Button>
      </Tooltip>
      {discount.active ? (
        <Tooltip label="Desativar desconto">
          <Button
            size="sm"
            colorScheme="red"
            variant="outline"
            isDisabled={discount.pending_approval}
            onClick={() => onToggle(discount)}
            leftIcon={<FontAwesomeIcon icon={faToggleOff} />}
          >
            Desativar
          </Button>
        </Tooltip>
      ) : (
        <Tooltip label="Reativar desconto">
          <Button
            size="sm"
            colorScheme="green"
            isDisabled={discount.pending_approval}
            onClick={() => onToggle(discount)}
            leftIcon={<FontAwesomeIcon icon={faToggleOn} />}
          >
            Reativar
          </Button>
        </Tooltip>
      )}
    </HStack>
  );
};

export function DiscountsPage(): JSX.Element {
  const { company, store } = useContext(CompanyContext) as any;
  const { data: discounts = [], isLoading, refetch } = useQuery<Discount[]>({
    queryKey: ["discounts"],
    queryFn: getMyDiscounts
  });
  const [isDiscountModalOpen, setIsDiscountModalOpen] = useState<boolean>(false);
  const [isToggleDiscountModalOpen, setIsToggleDiscountModalOpen] = useState<boolean>(false);
  const [selectedDiscount, setSelectedDiscount] = useState<Discount | null>(null);

  const discountTypes: DiscountTypeLabels = {
    buy_and_gain: "Compre e Ganhe",
    treat: "Mimo",
    discount: "Desconto na conta",
  };

  const isMobile = useBreakpointValue({ base: true, lg: false });

  const handleEditDiscount = (discount: Discount) => {
    setSelectedDiscount(discount);
    setIsDiscountModalOpen(true);
  };

  const handleToggleDiscount = (discount: Discount) => {
    setSelectedDiscount(discount);
    setIsToggleDiscountModalOpen(true);
  };

  const handleAddDiscount = () => {
    setSelectedDiscount(null);
    setIsDiscountModalOpen(true);
  };

  const closeDiscountModal = () => {
    setIsDiscountModalOpen(false);
    setSelectedDiscount(null);
    refetch();
  };

  const closeToggleModal = () => {
    setIsToggleDiscountModalOpen(false);
    setSelectedDiscount(null);
  };
  // Loading state
  if (isLoading) {
    return (
      <DashboardTemplate>
        <VStack spacing={6} width="100%" padding="20px 30px">
          <StoreStatistics />
          <GraphBox
            title="Meus Descontos"
            isLoading={true}
            subtext="Carregando seus descontos..."
          >
            <Center py={8}>
              <CircularProgress isIndeterminate color="green.300" />
            </Center>
          </GraphBox>
        </VStack>
      </DashboardTemplate>
    );
  }

  // Mobile card view
  const renderMobileCards = () => (
    <VStack spacing={4} width="100%">
      {discounts.map((discount) => (
        <Card key={discount.id} width="100%" variant="outline">
          <CardBody>
            <VStack align="stretch" spacing={3}>
              <Flex justify="space-between" align="center">
                <Text fontWeight="bold" fontSize="lg" noOfLines={1}>
                  {discount.name}
                </Text>
                <StatusBadge discount={discount} />
              </Flex>
              
              <Divider />
              
              <Stack spacing={2}>
                <Flex justify="space-between">
                  <Text fontSize="sm" color="gray.600">Modo:</Text>
                  <Badge variant="outline" textTransform="capitalize">
                    {discount.mode}
                  </Badge>
                </Flex>
                
                <Flex justify="space-between">
                  <Text fontSize="sm" color="gray.600">Tipo:</Text>
                  <Text fontSize="sm" textAlign="right">
                    {discountTypes[discount.type]}
                  </Text>
                </Flex>
                
                <Box>
                  <Text fontSize="sm" color="gray.600" mb={1}>Regulamento:</Text>
                  <Text fontSize="sm" noOfLines={3} bg="gray.50" p={2} borderRadius="md">
                    {discount.rules}
                  </Text>
                </Box>
              </Stack>
              
              <Divider />
              
              <DiscountActions
                discount={discount}
                company={company}
                store={store}
                onEdit={handleEditDiscount}
                onToggle={handleToggleDiscount}
              />
            </VStack>
          </CardBody>
        </Card>
      ))}
    </VStack>
  );

  // Desktop table view
  const renderDesktopTable = () => (
    <Box width="100%" overflowX="auto">
      <Table variant="simple" size="md">
        <Thead bg="gray.50">
          <Tr>
            <Th color="gray.700" fontWeight="bold">Nome</Th>
            <Th color="gray.700" fontWeight="bold">Modo</Th>
            <Th color="gray.700" fontWeight="bold">Tipo</Th>
            <Th color="gray.700" fontWeight="bold">Regulamento</Th>
            <Th color="gray.700" fontWeight="bold">Status</Th>
            <Th color="gray.700" fontWeight="bold">Ações</Th>
          </Tr>
        </Thead>
        <Tbody>
          {discounts.map((discount) => (
            <Tr key={discount.id} _hover={{ bg: "gray.50" }}>
              <Td fontWeight="medium" maxW="200px">
                <Text noOfLines={1} title={discount.name}>
                  {discount.name}
                </Text>
              </Td>
              <Td>
                <Badge variant="outline" textTransform="capitalize">
                  {discount.mode}
                </Badge>
              </Td>
              <Td>{discountTypes[discount.type]}</Td>
              <Td maxW="300px">
                <Text fontSize="sm" noOfLines={2} title={discount.rules}>
                  {discount.rules}
                </Text>
              </Td>
              <Td>
                <StatusBadge discount={discount} />
              </Td>
              <Td>
                <DiscountActions
                  discount={discount}
                  company={company}
                  store={store}
                  onEdit={handleEditDiscount}
                  onToggle={handleToggleDiscount}
                />
              </Td>
            </Tr>
          ))}
        </Tbody>
      </Table>
    </Box>
  );

  // Empty state
  const renderEmptyState = () => (
    <Center py={12}>
      <VStack spacing={4}>
        <FontAwesomeIcon icon={faExclamationTriangle} size="4x" color="#A0AEC0" />
        <Text fontSize="lg" color="gray.600" textAlign="center">
          Nenhum desconto encontrado
        </Text>
        <Text fontSize="sm" color="gray.500" textAlign="center" maxW="md">
          Você ainda não criou nenhum desconto. Comece criando seu primeiro desconto para atrair mais clientes!
        </Text>
      </VStack>
    </Center>
  );

  return (
    <DashboardTemplate>
      <VStack spacing={6} width="100%" padding="20px 30px">
        {/* Modals */}
        <DiscountModal
          isOpen={isDiscountModalOpen}
          initialData={selectedDiscount || {}}
          companyId={company.id}
          storeId={store.id}
          onClose={closeDiscountModal}
          refetch={refetch}
        />
        {selectedDiscount && (
          <ToggleDiscountModal
            isOpen={isToggleDiscountModalOpen}
            discount={selectedDiscount}
            onClose={closeToggleModal}
            refetch={refetch}
          />
        )}
        
        {/* Statistics */}
        <StoreStatistics />
        
        {/* Discounts Section */}
        <GraphBox
          title="Meus Descontos"
          padding={isMobile ? "16px" : "0px"}
          subtext="Aqui você pode desativar, alterar ou criar novos descontos. Lembre-se que toda alteração será avaliada pela nossa equipe antes de ser lançada."
        >
          {discounts.length === 0 ? (
            renderEmptyState()
          ) : isMobile ? (
            renderMobileCards()
          ) : (
            renderDesktopTable()
          )}
        </GraphBox>
        
        {/* Add Discount Button */}
        <Button
          colorScheme="green"
          position="fixed"
          bottom="50px"
          right="50px"
          borderRadius="2rem"
          height="60px"
          width={isMobile ? "60px" : "220px"}
          boxShadow="0px 5px 15px rgba(0,0,0,0.2)"
          onClick={handleAddDiscount}
          leftIcon={
            !isMobile ? (
              <FontAwesomeIcon
                style={{ paddingRight: "5px" }}
                size="lg"
                icon={faAdd}
              />
            ) : undefined
          }
          _hover={{
            transform: "scale(1.05)",
            boxShadow: "0px 8px 20px rgba(0,0,0,0.3)",
          }}
          transition="all 0.2s"
        >
          {isMobile ? (
            <FontAwesomeIcon size="lg" icon={faAdd} />
          ) : (
            "Adicionar Desconto"
          )}
        </Button>
      </VStack>
    </DashboardTemplate>
  );
}
